import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrderShipment } from '../../models/Order';
import { sortTrackingEventsByDateDesc, useOrderTracking } from '../../hooks/useOrderTracking';

interface TrackingModalProps {
  shipment?: OrderShipment;
}

export const TrackingModal = forwardRef<ModalCenterRef, TrackingModalProps>(({ shipment }, ref) => {
  const { t } = useTranslation('viewOrdersModule');
  const { data: tracking, isLoading, error, getEventIcon } = useOrderTracking();

  const courierName = shipment?.courier?.name || 'N/A';
  const trackingNumber = shipment?.tracking_number || 'N/A';

  const handleClose = () => {
    // Cerrar el modal usando la referencia
    if (ref && 'current' in ref && ref.current) {
      ref.current.closeModal();
    }
  };

  // Función para obtener el color del icono según el estado
  const getEventIconColor = () => {
    return 'text-icons';
  };

  // Función para obtener el color de fondo del icono
  const getEventIconBg = () => {
    return 'bg-lines';
  };

  return (
    <ModalCenter
      ref={ref}
      title={t('modals.tracking.title')}
      width="lg"
      type="default"
      fullScreenMobile={true}
      className="max-h-[90vh]"
    >
      <div className="flex flex-col h-full max-h-[80vh]">
        {/* Contenido principal */}
        <div className="flex-1 py-6 px-2 pb-24 md:pb-6 overflow-y-auto">
          {/* Header con información del courier */}
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="relative">
                <Avatar className="h-10 w-10 rounded-full bg-card-border flex items-center justify-center">
                  <AvatarImage src={shipment?.courier?.image_hash} alt={courierName} />
                  <AvatarFallback className="text-text">{courierName.substring(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
              </div>
            </div>
            <div className="flex flex-col pt-1">
              <span className="font-semibold text-text">{courierName}</span>
              <span className="text-sm text-subtext">{trackingNumber}</span>
            </div>
          </div>

          {/* Timeline de eventos */}
          <div className="mt-8 space-y-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <span className="text-sm text-complementary-text">{t('sections.tracking.loading')}</span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-8">
                <span className="text-sm text-subtext">{t('sections.tracking.error')}</span>
              </div>
            ) : tracking && tracking.events && tracking.events.length > 0 ? (
              sortTrackingEventsByDateDesc(tracking.events).map((event, index, sortedEvents) => (
                <div key={`${event.code}-${event.date}-${event.time}`} className="flex items-start gap-4">
                  <div className="flex-shrink-0 relative">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-full ${getEventIconBg()}`}>
                      <FontAwesomeIcon icon={getEventIcon(event.code)} className={`h-5 w-5 ${getEventIconColor()}`} />
                    </div>
                    {index < sortedEvents.length - 1 && (
                      <div className="absolute top-full left-1/2 h-8 w-px -translate-x-1/2 bg-lines" />
                    )}
                  </div>
                  <div className="flex flex-col pt-1">
                    <span className="font-semibold text-text">{event.description}</span>
                    <span className="text-sm text-subtext">
                      {event.date} {event.time}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center py-8">
                <span className="text-sm text-subtext">{t('sections.tracking.empty')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Botones de acción - Fijos en mobile */}
        <div className="fixed bottom-0 left-0 right-0 p-4 pt-0 md:pb-0 md:px-0 md:relative md:border-t-0">
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="lg" onClick={handleClose}>
              {t('modals.tracking.close')}
            </Button>
          </div>
        </div>
      </div>
    </ModalCenter>
  );
});

TrackingModal.displayName = 'TrackingModal';
